import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  ChevronsLeft,
  CreditCard,
  FileSignature,
  Brain,
  FileClock,
  FileText,
  Landmark,
  LayoutDashboard,
  Package,
  ReceiptText,
  RefreshCw,
  Scale,
  Settings as SettingsIcon,
  Users,
  Wallet,
  Warehouse,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/shadcn/sidebar';
import { AccountMenu } from './AccountMenu';
import { SidebarCredits } from './SidebarCredits';
import { SidebarControls } from './SidebarControls';
import { OrgSwitcher } from './OrgSwitcher';
import { useI18n } from '@/contexts/I18nContext';
import { useAppMode } from '@/contexts/AgentModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { can } from '@/lib/permissions';
import type { Capability } from '@/lib/permissions';
import { cn } from '@/lib/utils';

interface NavEntry {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  badge?: string;
  allow?: Capability;
  /** Hidden when the organization has this product switched off. */
  module?: string;
}

interface NavSection {
  groupKey: string;
  items: NavEntry[];
  /** Hidden entirely when the organization has this product switched off. */
  module?: string;
}

/**
 * The product suite, grouped by WHAT SOMEBODY IS DOING — not by which module
 * owns the table underneath.
 *
 * It used to be one "Finance" group of eight: Dashboard, Invoices, Bills,
 * Expenses, Payments, Bank, Chart of Accounts and Journal, stacked together.
 * Three things were wrong with that.
 *
 *   · THE DASHBOARD IS NOT FINANCE. It is the home of the whole app, and being
 *     inside a module-gated group meant a tenant who switched Finance off lost
 *     their front page. That was a bug, not a matter of taste.
 *   · EIGHT IS A WALL. Nobody reads a list that long; they scan for a word.
 *   · TWO DIFFERENT JOBS WERE MIXED. Raising an invoice and reading the journal
 *     are not the same activity, done by the same person, on the same day.
 *
 * Money you are OWED, money you OWE, money MOVING, and the ledger those three
 * feed — four short lists, each a question somebody actually arrives with.
 * Customers sit with invoices and vendors with bills, because that is where you
 * look for them; splitting "CRM" from "Finance" only made sense to us.
 *
 * `module` now sits on the ITEM as well as the group, so a tenant switching HR
 * off hides Employees and Payroll without taking a whole shelf with it, and a
 * group with nothing left to show hides itself.
 */
const SECTIONS: NavSection[] = [
  {
    // Ungrouped and never gated: whatever else is switched off, there is a
    // front door.
    groupKey: '',
    items: [{ to: '/dashboard', labelKey: 'dashboard', icon: LayoutDashboard }],
  },
  {
    groupKey: 'sales',
    items: [
      /* Distinct icons on purpose. These two sat next to each other with the
         same FileText glyph and near-identical labels ("Invoices & Quotes" over
         "Quotations"), so the only way to tell them apart was to read carefully
         — which is the one thing nobody does with a sidebar. */
      { to: '/invoices', labelKey: 'invoices', icon: FileText, allow: 'invoices.read', module: 'finance' },
      { to: '/quotations', labelKey: 'quotations', icon: FileClock, allow: 'quotations.read', module: 'crm' },
      { to: '/customers', labelKey: 'customers', icon: Users, allow: 'customers.read', module: 'crm' },
      { to: '/contracts', labelKey: 'contracts', icon: FileSignature, allow: 'contracts.read', module: 'crm' },
      /* Products carries NO module key on purpose. A product and service
         catalogue is what invoice lines are built from, so a contractor with
         Inventory switched off still sells "Site supervision, 40 hours" and
         still wants it saved. What the module gates is stock, not the
         catalogue — see the group below. */
      { to: '/items', labelKey: 'products', icon: Package, allow: 'items.read' },
    ],
  },
  {
    // Quantities, and where they are. Every entry here is stock, so the group
    // carries the module key and the whole shelf disappears with the product.
    groupKey: 'inventory',
    module: 'inventory',
    items: [
      { to: '/inventory/stock', labelKey: 'stock_levels', icon: Boxes, allow: 'stock.read' },
      { to: '/inventory/movements', labelKey: 'stock_movements', icon: ArrowLeftRight, allow: 'stock.read' },
      { to: '/inventory/adjustments', labelKey: 'adjustments', icon: Scale, allow: 'stock.read' },
      { to: '/inventory/warehouses', labelKey: 'warehouses', icon: Warehouse, allow: 'stock.read' },
    ],
  },
  {
    groupKey: 'purchases',
    items: [
      { to: '/bills', labelKey: 'bills', icon: ReceiptText, allow: 'bills.read', module: 'finance' },
      { to: '/expenses', labelKey: 'expenses', icon: Wallet, allow: 'expenses.read', module: 'finance' },
      { to: '/vendors', labelKey: 'vendors', icon: Building2, allow: 'vendors.read' },
    ],
  },
  {
    groupKey: 'money',
    items: [
      { to: '/payments', labelKey: 'payments', icon: CreditCard, allow: 'payments.read', module: 'finance' },
      { to: '/accounting/bank-accounts', labelKey: 'bank', icon: Landmark, module: 'finance' },
      { to: '/subscriptions', labelKey: 'subscriptions', icon: RefreshCw, allow: 'subscriptions.read' },
    ],
  },
  {
    // The ledger and what is read off it. Set-up-once territory, deliberately
    // below the things touched daily.
    groupKey: 'accounting',
    items: [
      { to: '/reports', labelKey: 'reports', icon: BarChart3, allow: 'reports.read' },
      { to: '/accounting/chart-of-accounts', labelKey: 'chart_of_accounts', icon: FileText, module: 'finance' },
      { to: '/accounting/journal', labelKey: 'journal', icon: BookOpen, module: 'finance' },
    ],
  },
  {
    groupKey: 'people',
    items: [
      { to: '/employees', labelKey: 'employees', icon: Users, allow: 'employees.read', module: 'hr' },
      { to: '/payroll', labelKey: 'payroll', icon: CreditCard, allow: 'payroll.read', module: 'hr' },
    ],
  },
  {
    groupKey: 'workspace',
    // People live inside Settings (the rail is a shared layout, so /users keeps
    // its URL). Billing does NOT — it is the subscription, not configuration,
    // and it is what an owner comes back to most often, so it gets its own
    // entry here rather than being two clicks deep inside Settings.
    items: [
      /* What the agent remembers. NOT behind settings.write — everyone can read
         the facts shaping their own answers, and only writing a company-wide
         one is gated (routes/erp/memory.ts). Burying it in Settings would make
         it something people discover after it has already been wrong. */
      { to: '/memory', labelKey: 'memory', icon: Brain, module: 'ai' },
      { to: '/billing', labelKey: 'settings_billing', icon: CreditCard, allow: 'settings.write' },
      { to: '/settings', labelKey: 'settings', icon: SettingsIcon, allow: 'settings.write' },
    ],
  },
];

export function Sidebar() {
  const { t, dir } = useI18n();
  const { user, hasModule } = useAuth();
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const { pathname } = useLocation();

  /** Mirrors NavLink's `end` semantics — /dashboard is exact, the rest match sub-routes. */
  const isActive = (to: string): boolean =>
    to === '/dashboard'
      ? pathname === '/dashboard' || pathname === '/'
      : pathname === to || pathname.startsWith(`${to}/`);

  const { mode, dockChat } = useAppMode();
  const collapsed = state === 'collapsed' && !isMobile;
  const tooltipSide = dir === 'rtl' ? 'left' : 'right';
  /*
    Going to a page LEAVES agent mode.

    Agent mode replaces the whole card with the conversation, so clicking
    "Invoices" while in it used to change the route behind a screen that was
    still showing a chat — the navigation appeared to do nothing. The chat is
    not lost: it drops back to the rail beside the page you asked for, which is
    what "I want to look at something while I talk" means.

    `dockChat()`, not `setMode('manual')`. Setting the mode is only half of it —
    the rail has its own open state in the shell, so plain manual mode landed
    people on the page with the conversation nowhere on screen. Docking asks the
    shell to bring it with them.
  */
  const closeOnNavigate = () => {
    if (isMobile) setOpenMobile(false);
    if (mode === 'agent') dockChat();
  };

  /*
    `inset`, not the default panel — the Linear shell.

    The sidebar stops being a bordered column beside the content and instead
    sits directly on the window background, with the content floating over it as
    a rounded card. The practical difference is that the app reads as ONE
    surface with a rail on it, rather than two panels butted together with a
    seam down the middle; and the card's edge does the work the border used to,
    so the seam disappears.
  */
  return (
    <ShadcnSidebar
      side={dir === 'rtl' ? 'right' : 'left'}
      collapsible="icon"
      variant="inset"
    >
      {/*
        The mark, then the company. The tile says which product this is; the
        chip beside it says which of your companies you are looking at, and is
        how you leave it for another. The full "Railit" wordmark is gone from
        here on purpose — it spent horizontal space telling you the name of the
        app you already have open, and left no room for the one label that
        changes. The wordmark still opens the dashboard, it is just the tile.
      */}
      <SidebarHeader className="gap-0 p-0">
        <div
          className={cn(
            'flex h-14 items-center gap-1.5 border-b border-sidebar-border px-2.5',
            collapsed && 'flex-col justify-center gap-1 px-0',
          )}
        >
          <NavLink
            to="/dashboard"
            onClick={closeOnNavigate}
            title="Railit"
            aria-label="Railit"
            className="flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent"
          >
            <Logo size={22} markOnly className="shrink-0" />
          </NavLink>
          {collapsed ? null : <OrgSwitcher />}
        </div>
      </SidebarHeader>

      <SidebarContent className="gap-0 py-3">
        {SECTIONS.map((section) => {
          if (section.module && !hasModule(section.module)) return null;
          const items = section.items.filter(
            (item) =>
              (!item.allow || can(user?.role, item.allow)) &&
              (!item.module || hasModule(item.module)),
          );
          // A shelf with nothing left on it is not a shelf.
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={section.groupKey} className="px-2.5 py-1.5 first:pt-0">
              {section.groupKey ? (
                <SidebarGroupLabel className="eyebrow mb-1 h-5 px-2.5 text-sidebar-foreground/45">
                  {t(section.groupKey)}
                </SidebarGroupLabel>
              ) : null}
              <SidebarGroupContent>
                <SidebarMenu className="gap-px">
                  {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      {/*
                        NO COLOUR in the rail. Active and hover are both a plain
                        neutral fill — the accent rail that used to mark the
                        current item made a list of sixteen links look like a
                        list of alerts, and in a sidebar the thing that says
                        "you are here" is contrast and weight, not hue. Colour is
                        saved for the one place it means something: the action.
                      */}
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.to)}
                        tooltip={{ children: t(item.labelKey), side: tooltipSide }}
                        className={cn(
                          'relative h-8 gap-2.5 px-2.5 text-[0.8125rem] text-sidebar-foreground/72',
                          'transition-colors [&>svg]:text-sidebar-foreground/45',
                          'hover:text-sidebar-foreground hover:[&>svg]:text-sidebar-foreground/70',
                          'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-foreground',
                                                                            )}
                      >
                        <NavLink
                          to={item.to}
                          onClick={closeOnNavigate}
                          end={item.to === '/dashboard'}
                        >
                          <item.icon />
                          <span>{t(item.labelKey)}</span>
                        </NavLink>
                      </SidebarMenuButton>
                      {item.badge ? <SidebarMenuBadge>{item.badge}</SidebarMenuBadge> : null}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarSeparator className="mx-0 bg-sidebar-border" />

      <SidebarFooter className="gap-1 p-2.5">
        {/* Credits sit directly above the account row — the two things you
            check without meaning to. See SidebarCredits for why it is a bar. */}
        <SidebarCredits />
        <SidebarSeparator className="my-1" />
        <SidebarMenu className="gap-px">
          {/* This row used to show your name and do nothing when clicked, while
              the real menu sat in the topbar. Now it IS the menu — one control,
              in the place people already reach for it. */}
          <SidebarMenuItem>
            <AccountMenu collapsed={collapsed} tooltipSide={tooltipSide} />
          </SidebarMenuItem>
          {/* Language, appearance, agent mode — the three app-level switches,
              together, at the foot of the rail. See SidebarControls. */}
          <SidebarMenuItem className="px-1 pt-1">
            <SidebarControls />
          </SidebarMenuItem>
          <SidebarMenuItem className="hidden md:block">
            <SidebarMenuButton
              onClick={toggleSidebar}
              tooltip={{
                children: collapsed ? t('expand_sidebar') : t('collapse_sidebar'),
                side: tooltipSide,
              }}
              className="h-8 gap-2.5 px-2.5 text-[0.8125rem] text-muted-foreground [&>svg]:text-muted-foreground/70"
            >
              <ChevronsLeft
                className={cn(
                  'transition-transform',
                  (collapsed ? dir === 'ltr' : dir === 'rtl') && 'rotate-180',
                )}
              />
              <span>{collapsed ? t('expand_sidebar') : t('collapse_sidebar')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </ShadcnSidebar>
  );
}
